import dateTimeFormat from "@/func/format/dateTimeFormat";
import getUtcDateTime from "@/func/format/getUtcDateTime";
import apiCall from "@/func/net/apiCall";
import { SfUser } from "@/types/generalTypes";
import {
  SfMathsCustomLevel,
  SfMathsLevel,
  SfMathsTestResult,
  SfMathTestQuestion,
} from "@/types/mathsTypes";
import { ref, computed } from "vue";

let lastSaveData = null as null | { 
  testId: number; 
  questionId: number; 
  question: SfMathTestQuestion; 
  answer: string 
};

const mathsGameState = ref({
  user: null as SfUser | null,
  level: null as SfMathsLevel | null,
  customLevel: null as SfMathsCustomLevel | null,
  questions: [] as SfMathTestQuestion[],
  progress: null as SfMathsTestResult | null,
  gameId: 0,
  currentQuestion: -1,
  saveStatus: 0,
  saveErrorMessage: '',
});

export const initMathsGameState = (
  user: SfUser | null,
  gameId: number,
  level?: SfMathsLevel | null,
  customLevel?: SfMathsCustomLevel | null,
) => {
  mathsGameState.value.user = user;
  mathsGameState.value.level = level ? level : null;
  mathsGameState.value.customLevel = customLevel ? customLevel : null;
  mathsGameState.value.gameId = gameId;
};

export const mathGameProgress = computed(() => mathsGameState.value.progress);
export const mathGameCurrentQuestionId = computed(
  () => mathsGameState.value.currentQuestion,
);
export const mathGameCurrentQuestion = computed(() => {
  return mathsGameState.value.questions[mathsGameState.value.currentQuestion];
});
export const mathsGameSaveStatus = computed(() => 
  mathsGameState.value.saveStatus
);
export const mathsSaveErrorMessage = computed(() => 
  mathsGameState.value.saveErrorMessage
)

export const setMathsGameSaveStatus = (status: number, message = '') => {
  mathsGameState.value.saveStatus = status;
  mathsGameState.value.saveErrorMessage = message;
  if(status == 3 && mathsGameState.value.progress) {
    mathsGameState.value.progress.completed = true;
  }
}

export const clearMathsGame = () => {
  mathsGameState.value.questions = [];
  mathsGameState.value.progress = null;
  setMathsGameSaveStatus(0);
};

export const loadMathsTest = async () => {
  const body = {
    levelId: mathsGameState.value.level ? mathsGameState.value.level.id : null,
    customLevelId: mathsGameState.value.customLevel
      ? mathsGameState.value.customLevel.id
      : null,
    gameId: mathsGameState.value.gameId,
  };
  const data = await apiCall(
    "sf-math-game/new-test",
    null,
    { method: "POST" },
    body
  );
  if (!data.success) {
    throw data.statusText;
  }
  mathsGameState.value.questions = JSON.parse(
    JSON.stringify(data.data.questions),
  );
  mathsGameState.value.progress = {
    id: data.data.testId,
    createdon: getUtcDateTime(),
    user_id: mathsGameState.value.user ? mathsGameState.value.user.id : null,
    question_count: data.data.questions.length,
    completed: false,
    total_answers: 0,
    correct_answers: 0,
    questions: [],
    student: mathsGameState.value.user ? mathsGameState.value.user : null,
  };
  mathsGameState.value.currentQuestion = -1;
  setMathsGameSaveStatus(0);
  return data.data;
};

export const getNextQuestion = () => {
  mathsGameState.value.currentQuestion =
    mathsGameState.value.currentQuestion + 1;
};

export const postMathsAnswer = async (
  testId: number,
  questionId: number,
  question: SfMathTestQuestion,
  answer: string,
) => {
  setMathsGameSaveStatus(1);
  const body = { testId, questionId, question, answer };
  const data = await apiCall(
    "sf-math-game/check-answer",
    null,
    { method: "POST" },
    body,
    null,
    true
  );
  if(data.success) {
    lastSaveData = null;
    setMathsGameSaveStatus(2);
    const newProgress = { ...mathsGameState.value.progress } as SfMathsTestResult;
    if (data.data?.completed) {
      newProgress.completed = true;
    }
    const progressQuestion = {
      questionKind: data.data?.questionKind,
      question: question.question,
      expectedAnswer: data.data?.correctAnswer,
      question_kind: data.data?.question_kind,
      answer: {
        isCorrect: data.data?.isCorrect,
        value: answer,
      },
      answerRender: data.data?.answerRender,
      answerKind: question.answerKind,
      answerUi: question.answerUi,
    };
    newProgress.questions?.push(progressQuestion);
    newProgress.total_answers = newProgress.total_answers
      ? newProgress.total_answers + 1
      : 1;
    if (data.data?.isCorrect) {
      newProgress.correct_answers = newProgress.correct_answers
        ? newProgress.correct_answers + 1
        : 1;
    }
    mathsGameState.value.progress = newProgress;
    return data.data;
  } else {
    if (data.httpStatus == 0) {
      setMathsGameSaveStatus(-1);
    } else {
      setMathsGameSaveStatus(-2, data.errorDetails?.message);
    }
    lastSaveData = { testId, questionId, question, answer };
  }
};

export const saveLastMathAnswerAgain = async () => {
  if(!lastSaveData) {
    setMathsGameSaveStatus(2);
    return;
  }
  const data = await postMathsAnswer(
    lastSaveData.testId,
    lastSaveData.questionId,
    lastSaveData.question,
    lastSaveData.answer,
  );
  if(data) {
    return data;
  }
}